import {
  PutObjectCommand,
  CreateMultipartUploadCommand,
  CompleteMultipartUploadCommand,
  UploadPartCommand,
} from '@aws-sdk/client-s3'
import { s3, setVideoUploadSession } from './client'
import { API_BASE_URL } from './utils/apiConfig'
import {
  initializeVideoRecordStatus,
  updateVideoRecordStatus,
  VideoRecordStatus,
} from '@/utils/videoRecordStatus'

// Event types that the hybrid uploader emits to notify the UI about upload progress
export interface HybridUploaderEvent {
  type:
    | 'progress'
    | 'chunk-uploaded'
    | 'chunk-saved'
    | 'error'
    | 'first-chunk-error'
    | 'state'
    | 'upload-id-available'
    | 'retry'
  progress?: { loaded: number; total?: number; part: number }
  chunkInfo?: { chunkNumber: number; size: number; etag: string; chunkKey?: string }
  error?: unknown
  state?: 'starting' | 'uploading' | 'resuming' | 'finalizing' | 'paused' | 'aborted' | 'completed'
  uploadId?: string
  chunkNumber?: number
  attempt?: number
}

export interface HybridLiveUploaderOptions {
  partSize?: number
  queueSize?: number
  maxRetries?: number
  retryDelay?: number
  firstChunkTimeout?: number
  saveUploadId?: (uploadId: string) => Promise<void>
  loadUploadId?: () => Promise<string | null>
  clearUploadId?: () => Promise<void>
  completeUploadEndpoint?: string
  onEvent?: (event: HybridUploaderEvent) => void
  saveChunks?: boolean // Enable/disable individual chunk storage
  employeeTestId?: string // For chunk tracking
  companyId?: string // For chunk tracking
  attemptNumber?: number // Which exam attempt this recording belongs to
  candidateToken?: string
}

export interface HybridLiveUploader {
  enqueue: (chunk: Blob) => Promise<void>
  done: () => Promise<string>
  abort: (reason?: any) => void
  getUploadId: () => string | null
  getBytesUploadedSoFar: () => number
  getChunkCount: () => number
  getUploadedParts: () => Array<{
    partNumber: number
    etag: string
    size: number
    chunkKey?: string
  }>
  getRunningUploads: () => number
  getQueueSize: () => number
  hasActiveUploads: () => boolean
  requestBackendCompletion: () => Promise<boolean>
}

export function createHybridLiveUploader(
  bucket: string,
  key: string,
  contentType: string,
  options: HybridLiveUploaderOptions = {}
): HybridLiveUploader {
  const {
    partSize = 5 * 1024 * 1024,
    queueSize = 4,
    maxRetries = 2,
    retryDelay = 1000,
    firstChunkTimeout = 60000,
    saveUploadId,
    loadUploadId,
    clearUploadId,
    completeUploadEndpoint = `${API_BASE_URL}/api/complete-upload`,
    onEvent,
    saveChunks = true, // Default to saving chunks
    employeeTestId,
    companyId,
    attemptNumber,
    candidateToken,
  } = options

  if (employeeTestId && candidateToken) {
    setVideoUploadSession(employeeTestId, candidateToken)
  }

  // Function to track chunk upload in database
  const trackChunkUpload = async (
    employeeTestId: string,
    uploadId: string,
    partNumber: number,
    etag: string,
    size: number,
    companyId?: string
  ): Promise<void> => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/video-uploads/chunk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          assignmentId: employeeTestId, candidateToken, uploadId, partNumber,
          etag, size, companyId, key,
        }),
      })
      if (!response.ok) throw new Error(`Chunk tracking failed: ${response.status}`)
    } catch (error) {
      console.warn(`[Hybrid Uploader] Error tracking chunk ${partNumber}:`, error)
      // Don't throw - chunk tracking failure shouldn't break the upload
    }
  }

  let uploadId: string | null = null
  let partNumber = 1
  let totalBytesUploaded = 0
  let chunkCount = 0
  let aborted = false
  let finalizing = false
  let initializingUpload = false // Guard against concurrent initialization

  const chunks: Blob[] = []
  const activeUploads = new Map<number, Promise<any>>()
  const uploadedParts: Array<{
    partNumber: number
    etag: string
    size: number
    chunkKey?: string
  }> = []

  const emit = (event: HybridUploaderEvent) => {
    try {
      onEvent?.(event)
    } catch (err) {
      console.error('[Hybrid Uploader] Event handler error:', err)
    }
  }

  // Helper function to update video record status
  const updateStatus = async (
    status: VideoRecordStatus,
    additionalData?: { bucket_path?: string; has_audio?: boolean }
  ) => {
    if (employeeTestId && uploadId) {
      try {
        await updateVideoRecordStatus(employeeTestId, uploadId, status, additionalData, candidateToken, key)
      } catch (error) {
        console.warn(`[Hybrid Uploader] Failed to update status to ${status}:`, error)
        // Don't fail the upload for status tracking errors
      }
    }
  }

  const initializeUpload = async (): Promise<string> => {
    // If we already have an upload ID, return it immediately
    if (uploadId) return uploadId

    // Prevent concurrent initialization attempts
    if (initializingUpload) {
      // Wait for the initialization to complete
      while (initializingUpload) {
        await new Promise((resolve) => setTimeout(resolve, 100))
      }
      if (uploadId) return uploadId
    }

    initializingUpload = true

    try {
      // Try to load existing upload ID
      if (loadUploadId) {
        const existingId = await loadUploadId()
        if (existingId) {
          uploadId = existingId
          emit({ type: 'upload-id-available', uploadId })
          emit({ type: 'state', state: 'resuming' })
          return uploadId
        }
      }

      // Create new multipart upload using S3 client
      try {
        const command = new CreateMultipartUploadCommand({
          Bucket: bucket,
          Key: key,
          ContentType: contentType,
        })

        const response = await s3.send(command)

        if (!response.UploadId) {
          throw new Error('No upload ID returned from S3')
        }

        uploadId = response.UploadId

        // Save upload ID
        if (saveUploadId) {
          try {
            await saveUploadId(uploadId)
          } catch (saveError) {
            console.warn(
              '[Hybrid Uploader] Failed to save upload ID (continuing anyway):',
              saveError
            )
            // Continue - we can still upload without saving the ID
          }
        }

        // Initialize video record status for multi_upload type
        if (employeeTestId && companyId) {
          try {
            await initializeVideoRecordStatus(employeeTestId, uploadId, key, companyId, attemptNumber, candidateToken)
          } catch (error) {
            console.warn('[Hybrid Uploader] Failed to initialize video record status:', error)
            // Don't fail the upload for status tracking errors
          }
        }

        emit({ type: 'upload-id-available', uploadId })
        emit({ type: 'state', state: 'uploading' })

        // Update status to uploading
        await updateStatus('uploading')

        return uploadId
      } catch (error) {
        console.error('[Hybrid Uploader] Failed to create multipart upload:', error)
        throw error
      }
    } finally {
      initializingUpload = false
    }
  }

  const saveIndividualChunk = async (chunk: Blob, chunkNumber: number): Promise<string | null> => {
    if (!saveChunks) return null

    try {
      // Generate chunk key
      const pathParts = key.split('/')
      const basePath = pathParts.slice(0, -1).join('/') // Remove filename
      const chunkKey = `${basePath}/chunk_${chunkNumber - 1}.webm` // Start from 0

      // Convert Blob to ArrayBuffer for S3 upload
      const arrayBuffer = await chunk.arrayBuffer()
      const uint8Array = new Uint8Array(arrayBuffer)

      const command = new PutObjectCommand({
        Bucket: bucket,
        Key: chunkKey,
        Body: uint8Array,
        ContentType: contentType,
      })

      await s3.send(command)

      emit({
        type: 'chunk-saved',
        chunkInfo: {
          chunkNumber,
          size: chunk.size,
          etag: '',
          chunkKey,
        },
      })

      return chunkKey
    } catch (error) {
      console.error(`[Hybrid Uploader] Failed to save individual chunk ${chunkNumber}:`, error)
      // Don't fail the main upload for chunk save errors
      return null
    }
  }
  const uploadChunkWithPartNumber = async (
    chunk: Blob,
    partNo: number,
    retryCount = 0
  ): Promise<void> => {
    if (aborted) throw new Error('Upload aborted')

    // Only increment chunkCount on first attempt (not on retries)
    if (retryCount === 0) {
      chunkCount++
    }

    try {
      await initializeUpload()

      const uploadPromise = (async () => {
        const arrayBuffer = await chunk.arrayBuffer()
        const uint8Array = new Uint8Array(arrayBuffer)

        const command = new UploadPartCommand({
          Bucket: bucket,
          Key: key,
          UploadId: uploadId!,
          PartNumber: partNo,
          Body: uint8Array,
        })

        return await s3.send(command)
      })()

      let response: Awaited<typeof uploadPromise>
      if (partNo === 1 && retryCount === 0) {
        const timeoutPromise = new Promise<never>((_, reject) =>
          setTimeout(
            () => reject(new Error(`First chunk timed out after ${firstChunkTimeout}ms`)),
            firstChunkTimeout
          )
        )
        response = await Promise.race([uploadPromise, timeoutPromise])
      } else {
        response = await uploadPromise
      }

      if (!(response as any).ETag) {
        throw new Error('No ETag returned from S3')
      }

      const etag = (response as any).ETag as string
      totalBytesUploaded += chunk.size

      uploadedParts.push({
        partNumber: partNo,
        etag,
        size: chunk.size,
      })

      if (employeeTestId && uploadId && companyId) {
        trackChunkUpload(employeeTestId, uploadId, partNo, etag, chunk.size, companyId).catch(
          (error) =>
            console.warn(`[Hybrid Uploader] Chunk tracking failed for part ${partNo}:`, error)
        )
      }

      emit({
        type: 'chunk-uploaded',
        chunkInfo: { chunkNumber: partNo, size: chunk.size, etag },
      })

      emit({
        type: 'progress',
        progress: { loaded: totalBytesUploaded, part: partNo },
      })

    } catch (error) {
      console.error(`[Hybrid Uploader] Chunk upload error (part ${partNo}):`, error)

      if (retryCount < maxRetries && !aborted) {
        emit({ type: 'retry', chunkNumber: partNo, attempt: retryCount + 1, error })

        // Exponential backoff: wait longer for each retry
        await new Promise((resolve) => setTimeout(resolve, retryDelay * (retryCount + 1)))

        // Retry with same part number
        return uploadChunkWithPartNumber(chunk, partNo, retryCount + 1)
      }

      // Max retries exceeded or aborted
      emit({ type: 'error', error })
      throw error
    }
  }

  const processQueue = async (): Promise<void> => {
    while (chunks.length > 0 && activeUploads.size < queueSize && !aborted) {
      const chunk = chunks.shift()!

      // Allocate part number once here
      const partNo = partNumber++
      // chunkCount++ happens in uploadChunkWithPartNumber

      const uploadPromise = uploadChunkWithPartNumber(chunk, partNo)
        .catch((error) => {
          console.error(`[Hybrid Uploader] Failed to upload part ${partNo}:`, error)

          if (partNo === 1) {
            emit({ type: 'first-chunk-error', error, chunkNumber: 1 })
          } else {
            emit({ type: 'error', error })
          }
        })
        .finally(() => {
          activeUploads.delete(partNo)
          if (!aborted) void processQueue()
        })

      activeUploads.set(partNo, uploadPromise)
    }
  }

  const completeUpload = async (): Promise<string> => {
    if (aborted) throw new Error('Upload aborted')
    if (finalizing) throw new Error('Upload already finalizing')

    finalizing = true
    emit({ type: 'state', state: 'finalizing' })

    // Update status to finalizing
    await updateStatus('finalizing')

    try {
      // Wait for all active uploads to complete
      await Promise.all(activeUploads.values())

      if (uploadedParts.length === 0) {
        throw new Error('No parts uploaded')
      }

      // Try client-side completion first using S3 client
      try {
        const command = new CompleteMultipartUploadCommand({
          Bucket: bucket,
          Key: key,
          UploadId: uploadId!,
          MultipartUpload: {
            Parts: uploadedParts
              .sort((a, b) => a.partNumber - b.partNumber) // Sort by part number ascending
              .map((part) => ({
                ETag: part.etag,
                PartNumber: part.partNumber,
              })),
          },
        })

        const response = await s3.send(command)

        // Clear upload ID
        if (clearUploadId) {
          try {
            await clearUploadId()
          } catch (clearError) {
            console.warn('[Hybrid Uploader] Failed to clear upload ID:', clearError)
            // Continue - not critical
          }
        }

        emit({ type: 'state', state: 'completed' })
        // Don't reset finalizing flag - keep it true to prevent multiple done() calls
        return response.Key || key
      } catch (clientError) {
        console.warn('[Hybrid Uploader] Client completion failed, trying backend:', clientError)

        // Fallback to backend completion
        const success = await requestBackendCompletion()
        if (success) {
          // Don't reset finalizing flag - keep it true to prevent multiple done() calls
          return key
        } else {
          // Update status to failed
          await updateStatus('failed')
          throw new Error('Both client and backend completion failed')
        }
      }
    } catch (error) {
      // Update status to failed on error
      await updateStatus('failed')
      emit({ type: 'error', error })
      throw error
    } finally {
      // Only reset finalizing on error, not on success
      // This prevents done() from being called multiple times
      // Check if we successfully completed by seeing if error was thrown
      const wasError = uploadedParts.length === 0 || aborted
      if (wasError) {
        finalizing = false
      }
    }
  }

  const requestBackendCompletion = async (): Promise<boolean> => {
    if (!uploadId) {
      console.error('[Hybrid Uploader] No upload ID for backend completion')
      return false
    }

    try {
      const response = await fetch(completeUploadEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          bucket,
          key,
          uploadId,
          companyId,
          employeeTestId,
          assignmentId: employeeTestId,
          candidateToken,
          chunkInfo: uploadedParts.map((part) => ({
            partNumber: part.partNumber,
            chunkKey: part.chunkKey,
            size: part.size,
            etag: part.etag,
          })),
        }),
      })

      const result = await response.json()

      if (response.ok && result.status === 'completed') {
        if (clearUploadId) {
          try {
            await clearUploadId()
          } catch (clearError) {
            console.warn('[Hybrid Uploader] Failed to clear upload ID:', clearError)
            // Continue - not critical
          }
        }

        emit({ type: 'state', state: 'completed' })
        return true
      } else {
        console.error('[Hybrid Uploader] Backend completion failed:', result)
        return false
      }
    } catch (error) {
      console.error('[Hybrid Uploader] Backend completion error:', error)
      return false
    }
  }

  return {
    async enqueue(chunk: Blob): Promise<void> {
      if (aborted) throw new Error('Upload aborted')

      chunks.push(chunk)
      await processQueue()
    },

    async done(): Promise<string> {
      return completeUpload()
    },

    abort(reason?: any): void {
      aborted = true

      // Clear queued chunks
      chunks.length = 0

      // Cancel active uploads (they will handle cleanup)
      activeUploads.clear()

      // Drop the cached upload ID so a future session doesn't try to reuse a
      // multipart upload tied to this aborted run.
      if (clearUploadId) {
        clearUploadId().catch((error) =>
          console.warn('[Hybrid Uploader] Failed to clear upload ID on abort:', error)
        )
      }

      // Update status to aborted
      updateStatus('aborted').catch((error) =>
        console.warn('[Hybrid Uploader] Failed to update status to aborted:', error)
      )

      emit({ type: 'state', state: 'aborted' })
    },

    getUploadId(): string | null {
      return uploadId
    },

    getBytesUploadedSoFar(): number {
      return totalBytesUploaded
    },

    getChunkCount(): number {
      return uploadedParts.length
    },

    getRunningUploads(): number {
      return activeUploads.size
    },

    getUploadedParts(): Array<{
      partNumber: number
      etag: string
      size: number
      chunkKey?: string
    }> {
      return uploadedParts
    },

    getQueueSize(): number {
      return chunks.length
    },

    hasActiveUploads(): boolean {
      return activeUploads.size > 0 || chunks.length > 0
    },

    requestBackendCompletion,
  }
}
